import React from 'react';
import { RADIUS, useTheme } from '../../theme';
import { baseButton } from './shared';

export type MenuItem = { key: string; label: React.ReactNode; icon?: React.ReactNode; danger?: boolean; disabled?: boolean; onSelect: () => void };

/**
 * Popover-meny under (eller med `placement="top"` över) en utlösare. Piltangenter flyttar mellan raderna, Escape och klick utanför stänger.
 * `trigger` får props som ska spridas på knappen som öppnar menyn.
 */
export function Menu({ trigger, items, align = 'left', width = 240, placement = 'bottom' }: {
  trigger: (p: { onClick: () => void; 'aria-haspopup': 'menu'; 'aria-expanded': boolean; ref: React.Ref<HTMLButtonElement> }) => React.ReactNode;
  items: MenuItem[]; align?: 'left' | 'right'; width?: number; placement?: 'bottom' | 'top';
}) {
  const T = useTheme();
  const [open, setOpen] = React.useState(false);
  const wrap = React.useRef<HTMLDivElement>(null);
  const btn = React.useRef<HTMLButtonElement>(null);
  const list = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[role=menuitem]:not([disabled])')?.focus();
    const onDown = (e: MouseEvent | TouchEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [open]);

  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const onKey = (e: React.KeyboardEvent) => {
    const els = Array.from(list.current?.querySelectorAll<HTMLElement>('[role=menuitem]:not([disabled])') ?? []);
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); els[(i + 1) % els.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); els[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); els[els.length - 1]?.focus(); }
    else if (e.key === 'Tab') close(false);
  };

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-block' }}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-haspopup': 'menu', 'aria-expanded': open, ref: btn })}
      {open && (
        <div ref={list} role="menu" onKeyDown={onKey} style={{
          position: 'absolute', [placement === 'top' ? 'bottom' : 'top']: 'calc(100% + 6px)', [align]: 0, zIndex: 1000, width, maxWidth: 'calc(100vw - 32px)',
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.md, boxShadow: T.shadow.float, padding: 6,
        }}>
          {items.map((it) => (
            <button key={it.key} role="menuitem" type="button" disabled={it.disabled}
              onClick={() => { close(); it.onSelect(); }}
              style={{
                ...baseButton, justifyContent: 'flex-start', width: '100%', minHeight: 44, padding: '0 12px', gap: 10,
                background: 'transparent', borderRadius: RADIUS.sm, fontSize: 15, fontWeight: 600, textAlign: 'left',
                color: it.danger ? T.danger : T.text, opacity: it.disabled ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.surface2; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {it.icon && <span style={{ display: 'flex', color: it.danger ? T.danger : T.textMuted }}>{it.icon}</span>}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
